import type { WindowConfiguration, WindowMeasurements, WindowSketchModel } from '@termovista/window-configurator'
import type { Opening } from '../openings/types'
import type { ShutterConfiguration } from '../../../shared/shutters'
import type { MosquitoNetConfiguration } from '../../../shared/mosquitoNets'
import type { DoorConfiguration, DoorWriteInput } from '../../../shared/doors'

export const MATERIAL_PREFERENCES = ['Aluminum', 'PVC', 'Wood', 'Composite', 'Other'] as const
export const PRICING_ITEM_ELEMENT_TYPES = ['window', 'door', 'shutter', 'mosquito_net', 'balcony_door', 'entrance_door', 'other'] as const
export type PricingItemElementType = typeof PRICING_ITEM_ELEMENT_TYPES[number]
export const TRIM_TYPES = ['Match Window', 'Other'] as const
export const TRIM_WIDTHS = ['30', '40', '50', '60'] as const

export interface PricingItem {
  shutter?: ShutterConfiguration
  mosquitoNet?: MosquitoNetConfiguration
  door?: DoorConfiguration
  associatedOpeningId?: string | null
  id: string
  pricingItemCode: string
  opportunityId: string
  openingId: string
  variantLabel: string
  displayLabel: string
  lineNumber: number
  status: 'draft' | 'requested' | 'priced' | 'selected' | 'discarded'
  elementType: PricingItemElementType | null
  supplierNotes: string | null
  quantity: number
  measurements: WindowMeasurements
  configuration: WindowConfiguration
  configurationStatus: { valid: boolean; warning: string | null }
  customerSelections: {
    materialPreference: string | null
    colorInsideId: string | null
    colorOutsideId: string | null
    colorInside: string | null
    colorOutside: string | null
  }
  glassConfiguration: { id: string; name: string | null; defaultBuildUp: string | null } | null
  productSelections: { frameProductLine: string | null; handleProductLine: string | null; handleColor: string | null; handleColorId?: string | null }
  requirements: {
    shutterRequired: boolean
    mosquitoNetRequired: boolean
    trimRequired: boolean
    trimType: string | null
    trimCustom: string | null
    trimWidthMm: string | null
  }
  sourceOpening: Pick<Opening, 'id' | 'openingCode' | 'opportunityId' | 'label' | 'room'> | null
  summary: { dimensions: string; operation: string; variant: string }
  differences: Array<{ field: string; openingValue: string; pricingItemValue: string }>
  history: { editable: boolean; deletable: boolean; reason: string | null }
  createdAt: string | null
  updatedAt: string | null
}

export type PricingItemPayload = {
  door?: DoorWriteInput
  shutter?: ShutterConfiguration
  mosquitoNet?: MosquitoNetConfiguration
  associatedOpeningId?: string | null
  elementType: PricingItemElementType | null
  supplierNotes: string | null
  quantity: number
  measurements: WindowMeasurements
  configuration: WindowConfiguration
  glassConfigurationId: string | null
  customerSelections: { materialPreference: string | null; colorInsideId: string | null; colorOutsideId: string | null }
  productSelections: { frameProductLine: string | null; handleProductLine: string | null; handleColor: string | null; handleColorId?: string | null }
  requirements: {
    shutterRequired: boolean
    mosquitoNetRequired: boolean
    trimRequired: boolean
    trimType: string | null
    trimCustom: string | null
    trimWidthMm: string | null
  }
}

export type PreparePricingResult = {
  created: PricingItem[]
  createdCount: number
  existingCount: number
  openingCount: number
  pricingItemCount: number
  missingOpeningCount: number
}

export const pricingItemSketchModel = (item: Pick<PricingItem, 'measurements' | 'configuration'> & Partial<PricingItem>): WindowSketchModel & Pick<PricingItem, 'elementType' | 'shutter' | 'mosquitoNet'> => ({
  elementType: item.elementType ?? null, shutter: item.shutter, mosquitoNet: item.mosquitoNet,
  measurements: item.measurements,
  configuration: item.configuration,
})
